<template>
    <div class="ai-agreement-container">
        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-flex-wrap rlms-gap-10 rlms-pb-30">
            <!-- Header with buttons -->
            <div class="rlms-flex rlms-flex-col rlms-gap-10">
                <h3 class="rlms-fs-lg rlms-fw-600">Artificial Intelligence Agreement (AIA)</h3>
                <p class="rlms-fs-sm">Required for AI feature access</p>
            </div>
        </div>
        <template v-if="!organization?.agreement">
            <div class="agreement-form rlms-mt-5">
                <div class="info-box-banner rlms-w-100">
                    <div class="info-box-content">
                        <h4 class="rlms-text-black">AI Usage Agreement</h4>
                        <p class="rlms-text-black">Accept the Artificial Intelligence Agreement to enable AI features
                            for
                            your organization. This agreement outlines data usage and privacy practices.</p>
                    </div>
                    <div class="info-box-banner-image">
                        <img src="@/assets/images/sb2.png" alt="">
                    </div>
                </div>
            </div>

            <div class="rlms-mt-5">
                <h4 class="rlms-mb-5">Review and Accept AI Agreement</h4>

                <!-- Agreement Preview -->
                <div class="agreement-preview-box">
                    <p class="rlms-text-secondary-base rlms-mb-15">
                        Before using AI features, you must read and accept our comprehensive AI Usage Agreement.
                        This agreement outlines important privacy protections and data handling procedures.
                    </p>

                    <div>
                        <button class="rlms-btn rlms-btn-outline-primary" @click="showAgreementModal = true">
                            <div class="rlms-flex rlms-items-center rlms-gap-5">
                                <span class="material-symbols-outlined">description</span>
                                Read Full Agreement
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Agreement Form -->
                <form @submit.prevent="acceptAgreement" class="rlms-flex rlms-flex-col rlms-gap-20">
                    <div class="rlms-flex rlms-flex-col rlms-gap-15">
                        <div class="rlms-flex rlms-flex-col rlms-gap-5">
                            <label>Your Name *</label>
                            <input type="text" placeholder="Enter your full name" required class="rlms-w-100"
                                v-model.trim="form.name" :disabled="!agreementRead" />
                        </div>

                        <div class="rlms-flex rlms-flex-col rlms-gap-5">
                            <label>Your Title *</label>
                            <input type="text" placeholder="Enter your job title" required class="rlms-w-100"
                                v-model.trim="form.title" :disabled="!agreementRead" />
                        </div>
                    </div>

                    <div class="agreement-checkbox-wrapper rlms-bg-extra-lightest rlms-p-15 rlms-rounded">
                        <label class="rlms-flex rlms-items-center rlms-gap-10">
                            <input type="checkbox" class="rlms-checkbox" required v-model="form.agreed"
                                :disabled="!agreementRead" />
                            <span class="rlms-text-secondary-base rlms-font-sm">
                                I have read and agree to the AI Agreement terms.
                                I am authorized to accept this agreement on behalf of my organization.
                            </span>
                        </label>
                    </div>

                    <div class="rlms-flex rlms-flex-col rlms-gap-10">
                        <div>
                            <button type="submit" class="rlms-btn rlms-btn-primary"
                                :disabled="!form.agreed || !agreementRead">
                                Accept Agreement
                            </button>
                        </div>

                        <p v-if="!form.agreed || !agreementRead"
                            class="rlms-font-xs rlms-text-danger-300 rlms-text-error-base">
                            You must read the full agreement before proceeding
                        </p>
                    </div>
                </form>

                <!-- Agreement Modal -->
                <BaseModal :title="`AI Usage Agreement`" @close="closeModal" @scrollEnd="disableAgreementBtn = false"
                    :visible="true" size="large" v-if="showAgreementModal">
                    <h4>ARTIFICIAL INTELLIGENCE USAGE AGREEMENT FOR HEALTHCARE DATA</h4>
                    <p>Effective Date: Current Date</p>
                    <section>
                        <h5>1. INTRODUCTION AND PURPOSE</h5>
                        <p>This Artificial Intelligence Usage Agreement ("Agreement") governs the use
                            of AI-powered features within the EESAPP application. By accepting this Agreement, your
                            organization acknowledges and agrees to the terms and conditions set forth herein regarding
                            the processing of learner data through AI systems, specifically OpenAI's services.</p>
                    </section>
                    <section>
                        <h5>2. DATA PROCESSING AND THIRD-PARTY INVOLVEMENT</h5>
                        <p>Your organization understands and agrees that:</p>
                        <ul>
                            <li>AI insights are powered by OpenAI's language models, which require
                                data to be transmitted to OpenAI's servers for processing.</li>
                            <li>All learner data is anonymized before transmission to protect
                                individual privacy and maintain HIPAA compliance.</li>
                            <li>Anonymized data may be retained by OpenAI for up to 30 days for
                                service improvement and security purposes.</li>
                            <li>Data deletion requests can be submitted, but complete removal from
                                all systems may take up to 30 days to process.</li>
                        </ul>
                    </section>
                    <section>
                        <h5>3. DATA ANONYMIZATION AND PROTECTION</h5>
                        <p>To ensure HIPAA compliance and protect learner privacy:</p>
                        <ul>
                            <li>All personally identifiable information (PII) is removed or replaced
                                with generic identifiers before AI processing.</li>
                            <li>Learner names, dates of birth, and other identifying information are
                                never transmitted to AI services.</li>
                            <li>Only assessment scores, developmental milestones, and non-identifying
                                behavioral data are processed.</li>
                        </ul>
                    </section>
                    <section>
                        <h5>4. CRITICAL USAGE REQUIREMENTS</h5>
                        <p>Users of AI features MUST adhere to the following requirements:</p>
                        <div class="warning-box">
                            <p><strong>DO NOT SHARE LEARNER NAMES OR IDENTIFYING
                                    INFORMATION</strong></p>
                            <p>Never input learner names, initials, or any other identifying
                                information into AI features. Doing so violates HIPAA regulations and this Agreement.
                                Users are responsible for maintaining anonymity when interacting with AI features.</p>
                        </div>
                    </section>
                    <section>
                        <h5>5. LIMITATIONS AND DISCLAIMERS</h5>
                        <p>Your organization acknowledges that:</p>
                        <ul>
                            <li>AI-generated insights are supplementary tools and do not replace
                                professional clinical judgment or formal assessments.</li>
                            <li>AI recommendations should be validated by qualified professionals
                                before implementation.</li>
                            <li>The accuracy of AI insights depends on the quality and completeness
                                of input data.</li>
                        </ul>
                    </section>
                    <section>
                        <h5>6. DATA RETENTION AND DELETION</h5>
                        <p>Anonymized data processed by AI services may be retained for up to 30
                            days. Organizations may request data deletion by contacting support, though complete removal
                            from all backup systems may take additional time. Local assessment data remains under your
                            organization's control and is not affected by AI data retention policies.</p>
                    </section>
                    <section>
                        <h5>7. COMPLIANCE AND SECURITY</h5>
                        <p>This AI service implementation maintains HIPAA compliance through
                            technical safeguards, anonymization protocols, and access controls. However, users must
                            follow proper procedures to maintain compliance. Any breach of protocol, including sharing
                            of identifying information, is the responsibility of the user and their organization.</p>
                    </section>
                    <section>
                        <h5>8. ACCEPTANCE AND AUTHORIZATION</h5>
                        <p>By accepting this Agreement, you certify that you are authorized to bind
                            your organization to these terms and that all users within your organization will be trained
                            on proper AI feature usage, particularly the prohibition against sharing identifying
                            information.</p>
                    </section>
                    <section>
                        <h5>9. MODIFICATIONS AND TERMINATION</h5>
                        <p>This Agreement may be updated to reflect changes in AI services or
                            regulatory requirements. Continued use of AI features after notification of changes
                            constitutes acceptance of modified terms. Organizations may terminate AI feature access at
                            any time by contacting support.</p>
                    </section>
                    <div
                        class="rlms-overflow-y-auto rlms-max-h-[60vh] rlms-mb-5 rlms-text-secondary-base rlms-text-sm rlms-leading-relaxed">
                        <p>
                            This AI Usage Agreement governs your access to and use of AI-powered features in our
                            platform.
                            By accepting, you acknowledge that your input data may be processed for AI model training
                            and
                            improvement purposes, following our strict privacy and security policies.
                        </p>
                        <p class="rlms-mt-3">
                            We ensure that all data handling complies with applicable data protection laws and that no
                            personally
                            identifiable information is shared with third-party AI providers without consent.
                        </p>
                        <p class="rlms-mt-3">
                            Please read this agreement carefully before proceeding.
                        </p>
                    </div>
                    <template #footer>

                        <div class="rlms-flex rlms-gap-3 rlms-items-center rlms-justify-between rlms-mt-4">
                            <div class="scroll-indicator">
                                <template v-if="disableAgreementBtn">
                                    <span class="material-symbols-outlined rlms-icon">arrow_downward</span>
                                    Please scroll to read the entire agreement
                                </template>
                            </div>

                            <button class="rlms-btn rlms-btn-primary" @click="closeModal(true)"
                                :disabled="disableAgreementBtn">
                                I Have Read the Agreement
                            </button>
                        </div>
                    </template>
                </BaseModal>
            </div>
        </template>
        <template v-else>
            <div class="agreement-form rlms-mt-5">
                <div class="info-box-banner active rlms-w-100">
                    <div class="info-box-content">
                        <h4 class="rlms-text-black">AI Agreement Active</h4>
                        <p class="rlms-text-black">Accepted on {{
                            formatDate(organization?.agreement?.accepted_at, 'MMMM D, YYYY')
                        }}</p>
                        <p class="rlms-text-black">Accepted by: {{ organization?.agreement?.name }}</p>
                    </div>
                    <div class="info-box-banner-image">
                        <img src="@/assets/images/sb.png" alt="">
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script setup>
import BaseModal from '@/components/BaseModal.vue'
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { formatDate } from '@/utils/format-helpers';
import { handleError } from '@/utils/handleError';
import { showToast } from '@/utils/swal';
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router';

const route = useRoute();
const organizationId = Number(route.params.organization ?? 0);
const organization = ref(null);
const showAgreementModal = ref(false)
const disableAgreementBtn = ref(true)
const agreementRead = ref(false)
const loader = useLoaderStore();

const form = ref({
    name: '',
    title: '',
    agreed: false
})

const closeModal = (markAsRead = false) => {
    showAgreementModal.value = false;
    if (markAsRead) {
        agreementRead.value = true;
    }
}

const fetchOrganization = async () => {
    try {
        loader.show();

        const { data } = await api.get(`/organizations/${organizationId}`);

        organization.value = data.data;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const acceptAgreement = async () => {
    if (!form.value.agreed || !agreementRead.value) return

    try {
        loader.show();
        await api.post(`/organizations/${organizationId}/agreement`, { ...form.value });
        await fetchOrganization();

        showToast({
            icon: 'success',
            title: 'Success',
            text: 'AI Agreement accepted successfully!'
        })

    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

onMounted(async () => {
    await fetchOrganization();
});
</script>
<style scoped>
.info-box-banner {
    display: flex;
    align-items: center;
    gap: 24px;
    overflow: hidden;
    background: #FFECEC;
    border-radius: 20px;
    justify-content: space-between;
    text-align: left;
}

.info-box-banner.active {
    background: #E8F5E9
}

.agreement-preview-box {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid;
    padding: 15px 25px;
    border-radius: 20px;
    background: #fff5f1;
    margin-bottom: 20px;
    border-color: #8f959c;
}

.info-box-content {
    padding-left: 25px;
}

.info-box-banner-image img {
    width: 400px;
}

.popup__content__box section {
    margin-top: 15px;
}

.scroll-indicator .rlms-icon {
    display: inline-block;
    animation: bounce 1s ease-in-out infinite;
}

/* Simple bounce effect */
@keyframes bounce {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(6px);
    }
}
</style>